import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { ThreeDotsIcon } from '../../assets/icons/index';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '../popover/index';
import { Tooltip } from '../tooltip/index';

type ActionConfig = {
  attributes?: Record<string, unknown>;
  className?: string;
  disabled?: boolean;
  tooltipContent?: ReactNode;
  wrapperClassName?: string;
} & OR<
  {
    callback: () => void;
    icon?: ReactNode;
    title: string;
  },
  { component: ReactNode; key: string }
>;

type ActionItem = ActionConfig | null;

export type ActionsProps = {
  actions: ActionItem[];
};

export const Actions: FC<ActionsProps> = ({ actions }) => (
  <Popover>
    <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
      <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
        <ThreeDotsIcon />
      </div>
    </PopoverTrigger>
    <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-1 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
      {actions.map((action) => {
        if (!action) return null;

        const {
          callback,
          title,
          icon,
          className,
          attributes,
          component,
          key,
          wrapperClassName,
          disabled,
          tooltipContent,
        } = action;

        const actionNode = (
          <PopoverClose
            className={twMerge(
              'flex w-full items-center justify-start gap-2 p-[6px] pr-6 text-sm text-gray-700 hover:bg-gray-50',
              wrapperClassName,
              disabled && 'bg-white text-gray-300 hover:bg-white',
            )}
            disabled={disabled}
            key={key || title}
            onClick={callback}
            {...attributes}
          >
            {component ? (
              component
            ) : (
              <>
                {icon}
                <span className={className}>{title}</span>
              </>
            )}
          </PopoverClose>
        );

        if (!disabled || !tooltipContent) return actionNode;

        return (
          <Tooltip className="w-[300px] max-w-[300px]" content={tooltipContent}>
            <div className="w-full">{actionNode}</div>
          </Tooltip>
        );
      })}
    </PopoverContent>
  </Popover>
);
